import { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, Info, Bell, RefreshCw, PhoneMissed, Cpu, Check, CheckCheck } from 'lucide-react';
import { getAlerts } from '../services/portalApi';

const SEVERITY = {
  critical: { color: 'var(--err)', bg: 'var(--err-bg)', border: 'var(--err-border)', icon: <AlertTriangle size={15} color="var(--err)"/> },
  warn:     { color: 'var(--warn)', bg: 'var(--warn-bg)', border: '#92400e44', icon: <AlertTriangle size={15} color="var(--warn)"/> },
  info:     { color: 'var(--info)', bg: '#0f1830', border: '#2563eb33', icon: <Info size={15} color="var(--info)"/> },
};

function fmtTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return ''; }
}

export default function AlertsPage({ user, liveAlerts = [], ackedIds = new Set(), onAcknowledge, onAcknowledgeAll }) {
  const [alerts,      setAlerts]      = useState(liveAlerts);
  const [loading,     setLoading]     = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Sync when parent refreshes live data
  useEffect(() => { setAlerts(liveAlerts); }, [liveAlerts]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { getAlerts: fetch } = await import('../services/portalApi');
      const data = await fetch();
      setAlerts(data.alerts || []);
      setLastUpdated(new Date());
    } catch {}
    finally { setLoading(false); }
  }, []);

  // Self-poll when on this page (30s)
  useEffect(() => {
    setLastUpdated(new Date());
    const id = setInterval(async () => {
      try {
        const data = await getAlerts();
        setAlerts(data.alerts || []);
        setLastUpdated(new Date());
      } catch {}
    }, 30000);
    return () => clearInterval(id);
  }, []);

  const unacked   = alerts.filter(a => !ackedIds.has(a.id));
  const acked     = alerts.filter(a => ackedIds.has(a.id));
  const warnings  = unacked.filter(a => a.severity === 'warn' || a.severity === 'critical');
  const failedTotal = unacked.filter(a => a.category === 'calls').reduce((s, a) => s + (a.count || 0), 0);
  const infos     = unacked.filter(a => a.severity === 'info');

  function AlertRow({ a, isAcked }) {
    const cfg = SEVERITY[a.severity] || SEVERITY.info;
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        background: isAcked ? 'var(--surface-2)' : cfg.bg,
        border: `1px solid ${isAcked ? 'var(--border)' : cfg.border}`,
        borderRadius: 8, padding: '10px 14px', marginBottom: 6,
        opacity: isAcked ? 0.45 : 1,
        transition: 'opacity 0.2s',
      }}>
        <div style={{ flexShrink: 0 }}>{cfg.icon}</div>
        <div style={{ flex: 1, fontSize: 13, color: isAcked ? 'var(--muted-3)' : 'var(--text-2)' }}>{a.msg}</div>
        {a.ts && <span style={{ fontSize: 11, color: 'var(--muted-3)', flexShrink: 0 }}>{fmtTime(a.ts)}</span>}
        {!isAcked && (
          <button
            onClick={() => onAcknowledge?.(a.id)}
            title="Acknowledge"
            style={{
              flexShrink: 0, background: 'var(--border)', border: '1px solid var(--border-strong)',
              borderRadius: 6, color: 'var(--muted-2)', cursor: 'pointer',
              padding: '3px 8px', fontSize: 11, display: 'flex', alignItems: 'center', gap: 4,
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--ok)'; e.currentTarget.style.borderColor = 'var(--ok)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--muted-2)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
          >
            <Check size={11}/> Ack
          </button>
        )}
        {isAcked && <span style={{ fontSize: 10, color: 'var(--muted-3)', flexShrink: 0, fontStyle: 'italic' }}>acknowledged</span>}
      </div>
    );
  }

  return (
    <div className="content">
      <div className="stats-grid mb-20" style={{ gridTemplateColumns: 'repeat(3,1fr)' }}>
        {[
          { label: 'Active Warnings',   value: warnings.length, color: 'var(--warn)', icon: <AlertTriangle size={18} color="var(--warn)"/> },
          { label: 'Failed Calls (1h)', value: failedTotal,     color: 'var(--err)', icon: <PhoneMissed   size={18} color="var(--err)"/> },
          { label: 'Info',              value: infos.length,    color: 'var(--info)', icon: <Info           size={18} color="var(--info)"/> },
        ].map(s => (
          <div key={s.label} className="stat-card">
            <div className="stat-header">
              <span className="stat-label">{s.label}</span>
              <div className="stat-icon" style={{ background: 'var(--border)' }}>{s.icon}</div>
            </div>
            <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Bell size={16} color="var(--info)"/>
            <div className="card-title">Live Alerts</div>
            {lastUpdated && (
              <span style={{ fontSize: 11, color: 'var(--muted-3)' }}>
                · updated {fmtTime(lastUpdated)}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {unacked.length > 0 && (
              <button
                className="btn btn-sm"
                onClick={() => onAcknowledgeAll?.()}
                style={{ display: 'flex', alignItems: 'center', gap: 4 }}
              >
                <CheckCheck size={11}/> Acknowledge All
              </button>
            )}
            <button className="btn btn-sm" onClick={load} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <RefreshCw size={11} style={loading ? { animation: 'spin 0.8s linear infinite' } : {}}/>
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>
        <div className="card-body">
          {alerts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '50px 0' }}>
              <div style={{ color: 'var(--ok)', fontSize: 14, fontWeight: 600, marginBottom: 6 }}>All clear</div>
              <div style={{ color: 'var(--muted-3)', fontSize: 12 }}>No active alerts. Refreshes every 30 seconds.</div>
            </div>
          ) : (
            <>
              {/* SBC resource alerts */}
              {alerts.filter(a => a.category === 'sbc').length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <Cpu size={13} color="var(--info)"/>
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>SBC Resources</span>
                  </div>
                  {alerts.filter(a => a.category === 'sbc').map(a => (
                    <AlertRow key={a.id} a={a} isAcked={ackedIds.has(a.id)}/>
                  ))}
                </div>
              )}

              {/* Failed call alerts */}
              {alerts.filter(a => a.category === 'calls').length > 0 && (
                <div style={{ marginBottom: acked.length > 0 ? 16 : 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <PhoneMissed size={13} color="var(--info)"/>
                    <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--info)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Failed Calls — Last Hour</span>
                  </div>
                  {alerts.filter(a => a.category === 'calls').map(a => (
                    <AlertRow key={a.id} a={a} isAcked={ackedIds.has(a.id)}/>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
